import {
  Paper,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Badge,
  Skeleton,
  useTheme,
  Tooltip,
} from "@mui/material"
import { CheckCircle } from "@mui/icons-material"
import { ChatViewer } from "../interfaces"
import { format } from "date-fns"
import { ru } from "date-fns/locale"
import { FixedSizeList, ListChildComponentProps } from "react-window"
import { useRef, useState, useEffect } from "react"

const ITEM_HEIGHT = 80

interface ChatListProps {
  chats: ChatViewer.Chat[]
  selectedChatId: string | null
  onChatSelect: (chatId: string) => void
  loading?: boolean
  viewedChats: Record<string, number>
  getNewMessagesCount: (chat: ChatViewer.Chat) => number
}

const getChatDisplayName = (chat: ChatViewer.Chat): string => {
  if (chat.user_info) {
    return `ID: ${chat.user_info.telegram_id}`
  }
  return chat.chat_id.substring(0, 8) + "..."
}

const getLastMessage = (chat: ChatViewer.Chat): string => {
  if (chat.messages.length === 0) return "Нет сообщений"
  const lastMsg = chat.messages[chat.messages.length - 1]
  return lastMsg.message.length > 50
    ? lastMsg.message.substring(0, 50) + "..."
    : lastMsg.message
}

const getLastMessageTime = (chat: ChatViewer.Chat): string => {
  if (chat.messages.length === 0) return ""
  const lastMsg = chat.messages[chat.messages.length - 1]
  return format(new Date(lastMsg.created_at), "dd.MM HH:mm", { locale: ru })
}

interface RowData {
  chats: ChatViewer.Chat[]
  selectedChatId: string | null
  onChatSelect: (chatId: string) => void
  viewedChats: Record<string, number>
  getNewMessagesCount: (chat: ChatViewer.Chat) => number
}

const ChatRow: React.FC<ListChildComponentProps<RowData>> = ({
  index,
  style,
  data,
}) => {
  const theme = useTheme()
  const {
    chats,
    selectedChatId,
    onChatSelect,
    viewedChats,
    getNewMessagesCount,
  } = data
  const chat = chats[index]

  const isViewed = chat.chat_id in viewedChats
  const newCount = getNewMessagesCount(chat)
  const totalCount = chat.messages.length

  const renderBadge = () => {
    if (!isViewed) {
      return <Badge badgeContent={totalCount} color="primary" sx={{ ml: 2 }} />
    }
    if (newCount > 0) {
      return (
        <Box sx={{ ml: 2, position: "relative", display: "inline-flex" }}>
          <Tooltip title={`Всего: ${totalCount}`}>
            <Badge badgeContent={totalCount} color="default">
              <Badge
                badgeContent={newCount}
                color="primary"
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
              />
            </Badge>
          </Tooltip>
        </Box>
      )
    }
    return (
      <Box sx={{ ml: 2, display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box
          sx={{
            bgcolor: theme.palette.action.selected,
            color: theme.palette.text.secondary,
            borderRadius: "10px",
            px: 0.75,
            minWidth: 20,
            height: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {totalCount}
        </Box>
        <Tooltip title="Просмотрено">
          <CheckCircle
            sx={{ color: theme.palette.success.main, fontSize: 16 }}
          />
        </Tooltip>
      </Box>
    )
  }

  return (
    <div style={style}>
      <ListItemButton
        selected={selectedChatId === chat.chat_id}
        onClick={() => onChatSelect(chat.chat_id)}
        sx={{
          height: ITEM_HEIGHT,
          borderBottom: `1px solid ${theme.palette.divider}`,
          "&.Mui-selected": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.action.selected
                : theme.palette.primary.light + "20",
          },
        }}
      >
        <ListItemText
          primary={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2" noWrap>
                {getChatDisplayName(chat)}
              </Typography>
              {renderBadge()}
            </Box>
          }
          secondary={
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ mt: 0.5 }}
              >
                {getLastMessage(chat)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getLastMessageTime(chat)}
              </Typography>
            </Box>
          }
        />
      </ListItemButton>
    </div>
  )
}

/** Измеряет контейнер через ResizeObserver и передаёт размеры в FixedSizeList */
const SizedChatList: React.FC<{ itemData: RowData }> = ({ itemData }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      setDimensions({ width, height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <Box ref={containerRef} sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
      {dimensions.height > 0 && (
        <FixedSizeList
          height={dimensions.height}
          width={dimensions.width}
          itemCount={itemData.chats.length}
          itemSize={ITEM_HEIGHT}
          itemData={itemData}
          overscanCount={5}
        >
          {ChatRow}
        </FixedSizeList>
      )}
    </Box>
  )
}

export const ChatList: React.FC<ChatListProps> = ({
  chats,
  selectedChatId,
  onChatSelect,
  loading = false,
  viewedChats,
  getNewMessagesCount,
}) => {
  const theme = useTheme()

  if (loading) {
    return (
      <Paper sx={{ height: "70vh", p: 2 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 2 }} />
        ))}
      </Paper>
    )
  }

  if (!chats || chats.length === 0) {
    return (
      <Paper
        sx={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Box textAlign="center">
          <Typography variant="body1" color="text.secondary">
            Нет доступных чатов
          </Typography>
        </Box>
      </Paper>
    )
  }

  const itemData: RowData = {
    chats,
    selectedChatId,
    onChatSelect,
    viewedChats,
    getNewMessagesCount,
  }

  return (
    <Paper sx={{ height: "70vh", display: "flex", flexDirection: "column" }}>
      {/* Шапка */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Чаты
        </Typography>
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            borderRadius: "12px",
            px: 1.25,
            py: 0.25,
            minWidth: 28,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" fontWeight={700} lineHeight={1.6}>
            {chats.length}
          </Typography>
        </Box>
      </Box>

      {/* Виртуализированный список */}
      <SizedChatList itemData={itemData} />
    </Paper>
  )
}
